<script setup lang="ts">
// Vue
import { ref, watch } from "vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";

// Features
import InfoIcon from "@/features/Tooltip/InfoIcon.vue";

// Components
import InputLabel from "./InputLabel.vue";

const props = defineProps<{
  label?: string;
  // Keeps `label` as the input's accessible name while dropping the visible label row.
  hideLabel?: boolean;
  options: { name: string; isFavorite: boolean }[];
  keepAfterSelect?: boolean;
  errorMsg?: string;
  required?: boolean;
  placeholder?: string;
  value: string | undefined;
}>();

const emits = defineEmits(["focusIn", "optionSelected"]);

const allOptions = ref<{ name: string; isFavorite: boolean }[]>(props.options);
const options = ref<{ name: string; isFavorite: boolean }[]>(props.options);
const isHidden = ref<boolean>(true);
const currentlyHoveredIndex = ref<number>(0);
const id = ref<string>(uuidv4());

const searchString = ref<string>(props.value ?? "");

const getNewOptions = () => {
  const newOptions = allOptions.value.filter((option) => {
    if (option !== null && option.name.toLowerCase().includes(searchString.value.toLowerCase())) return true;
  });
  options.value = newOptions;
};

const handleReset = () => {
  emits("optionSelected", undefined);
};

watch(searchString, (_newSearchString, _oldSearchString) => {
  getNewOptions();
});

watch(
  () => props.options,
  (newOptions) => {
    allOptions.value = newOptions;
    options.value = newOptions;
    getNewOptions();
  },
  {
    deep: true,
  }
);

const handleFocusIn = () => {
  emits("focusIn");
  showOptions();
};

const showOptions = () => {
  isHidden.value = false;
};
const hideOptions = () => {
  setTimeout(() => {
    isHidden.value = true;
  }, 200);
};

const handleNewSelected = (option: { name: string; isFavorite: boolean }) => {
  if (!option) return;

  searchString.value = option.name;
  emits("optionSelected", option.name);
  if (!props.keepAfterSelect) {
    searchString.value = "";
  }
  isHidden.value = true;
};

const nextItem = (event: any) => {
  isHidden.value = false;
  if (event.keyCode !== 38 && event.keyCode !== 40) return;
  if (event.keyCode === 38) {
    if (currentlyHoveredIndex.value <= 0) return;
    currentlyHoveredIndex.value--;
  } else {
    if (currentlyHoveredIndex.value >= options.value.length - 1) return;
    currentlyHoveredIndex.value++;
  }

  if (currentlyHoveredIndex.value % 4 === 0) {
    const focusedElement = document.getElementById(`${id.value}-${currentlyHoveredIndex.value}`);
    focusedElement?.scrollIntoView({ behavior: "smooth" });
  }
};
</script>

<template>
  <div class="w-full">
    <div class="flex flex-col">
      <div
        v-if="!hideLabel"
        class="flex items-baseline gap-2">
        <InputLabel
          :id="id"
          :label="label"
          :required="required" />
        <InfoIcon tooltip="The projects in the list is the projects that you have access to within your organisation." />
      </div>

      <div class="relative w-full">
        <input
          :id="id"
          v-model="searchString"
          type="text"
          name="input"
          :aria-label="label || 'Search projects'"
          :class="[errorMsg ? 'border-app-red-500' : 'border-slate-300']"
          autocomplete="off"
          :placeholder="placeholder ? placeholder : undefined"
          class="focus:border-app-blue-600 rounded-8 h-9 w-full border p-2 text-sm leading-none outline-hidden"
          data-cy="search-select-automap-projects-input"
          @change="handleReset"
          @keyup="nextItem"
          @keyup.enter="handleNewSelected(options[currentlyHoveredIndex])"
          @focusin="handleFocusIn"
          @focusout="hideOptions" />
        <font-awesome-icon
          icon="fa-solid fa-search"
          class="text-text-color absolute top-1/2 right-4 -translate-y-1/2" />
      </div>
    </div>
    <span
      v-if="errorMsg"
      class="text-app-red-700 text-xs"
      >{{ errorMsg }}</span
    >
    <div class="relative">
      <div
        v-if="!isHidden"
        class="rounded-16 absolute z-50 max-h-44 w-full overflow-y-scroll border border-slate-200 bg-slate-100 shadow-xl">
        <div
          v-for="(option, index) in options"
          :id="`${id}-${index}`"
          :key="option.name"
          class="w-full cursor-pointer p-2 text-sm"
          :class="{ 'bg-slate-100': index === currentlyHoveredIndex }"
          data-cy="search-select-option"
          @mouseenter="currentlyHoveredIndex = index"
          @click="handleNewSelected(option)">
          <div class="flex w-full justify-between">
            <span>{{ option.name }}</span
            ><font-awesome-icon
              v-if="option.isFavorite"
              class="text-app-yellow-400 h-6 w-6"
              icon="fa-solid fa-star" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
